import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import api from '../api/client';

interface CompanyRow {
  id: string;
  name: string;
  cnpj?: string | null;
  phone?: string | null;
  status: 'ATIVA' | 'SUSPENSA';
  plan: string;
  createdAt: string;
  _count: { users: number; vehicles: number; routes: number };
}

interface Metrics {
  companies: number;
  activeCompanies: number;
  users: number;
  activeUsers: number;
  routes: number;
  vehicles: number;
}

interface CreateResult {
  company: CompanyRow;
  admin: { email: string };
  tempPassword: string;
}

export default function AdminPanel() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);

  const [editing, setEditing] = useState<CompanyRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CompanyRow | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<CompanyRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editCnpj, setEditCnpj] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPlan, setEditPlan] = useState('STANDARD');

  const [companyName, setCompanyName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [plan, setPlan] = useState('STANDARD');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get<Metrics>('/admin/metrics'),
        api.get<CompanyRow[]>('/admin/companies')
      ]);
      setMetrics(m.data);
      setCompanies(c.data);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao carregar painel', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openUsers = (c: CompanyRow) => {
    navigate(`/usuarios?companyId=${encodeURIComponent(c.id)}`);
  };

  const openEdit = (c: CompanyRow) => {
    setEditing(c);
    setEditName(c.name);
    setEditCnpj(c.cnpj || '');
    setEditPhone(c.phone || '');
    setEditPlan(c.plan || 'STANDARD');
  };

  const handleEdit = async () => {
    if (!editing) return;
    if (!editName.trim()) {
      showToast('Nome é obrigatório.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/companies/${editing.id}`, {
        name: editName.trim(),
        cnpj: editCnpj.trim() || null,
        phone: editPhone.trim() || null,
        plan: editPlan
      });
      showToast('Empresa atualizada.', 'success');
      setEditing(null);
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao atualizar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (c: CompanyRow) => {
    try {
      await api.delete(`/admin/companies/${c.id}`);
      showToast('Empresa removida.', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao remover', 'error');
    }
  };

  const doToggleStatus = async (c: CompanyRow) => {
    const next = c.status === 'ATIVA' ? 'SUSPENSA' : 'ATIVA';
    try {
      await api.put(`/admin/companies/${c.id}/status`, { status: next });
      showToast('Status atualizado.', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao atualizar', 'error');
    }
  };

  const handleCreate = async () => {
    if (!companyName.trim() || !adminName.trim() || !adminEmail.trim()) {
      showToast('Preencha empresa, admin e email.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post<CreateResult>('/admin/companies', {
        companyName: companyName.trim(),
        cnpj: cnpj.trim() || null,
        phone: phone.trim() || null,
        plan,
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim().toLowerCase()
      });
      setCreateResult(data);
      setCreateOpen(false);
      setCompanyName(''); setCnpj(''); setPhone(''); setPlan('STANDARD');
      setAdminName(''); setAdminEmail('');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao criar empresa', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copy = (v: string) =>
    navigator.clipboard.writeText(v).then(
      () => showToast('Copiado.', 'success'),
      () => showToast('Não foi possível copiar.', 'error')
    );

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1>Painel do SaaS</h1>
          <p className="sub">Área do dono da plataforma. Gerencie empresas cliente, veja uso e dê suporte.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          Nova empresa
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat">
          <div className="lbl">Empresas</div>
          <div className="val">{metrics?.companies ?? '…'}</div>
          <div className="foot">{metrics?.activeCompanies ?? 0} ativas</div>
        </div>
        <div className="stat">
          <div className="lbl">Usuários</div>
          <div className="val">{metrics?.users ?? '…'}</div>
          <div className="foot">{metrics?.activeUsers ?? 0} ativos</div>
        </div>
        <div className="stat">
          <div className="lbl">Veículos</div>
          <div className="val">{metrics?.vehicles ?? '…'}</div>
        </div>
        <div className="stat">
          <div className="lbl">Rotas</div>
          <div className="val">{metrics?.routes ?? '…'}</div>
        </div>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          {loading ? (
            <div className="empty" style={{ padding: 24 }}>Carregando…</div>
          ) : companies.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>Nenhuma empresa cliente cadastrada ainda.</div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Plano</th>
                  <th>Status</th>
                  <th>Usuários</th>
                  <th>Veículos</th>
                  <th>Rotas</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div>{c.name}</div>
                      <small style={{ opacity: 0.6 }}>{c.cnpj || 'CNPJ não informado'}</small>
                    </td>
                    <td>{c.plan}</td>
                    <td>
                      <span className={`badge ${c.status === 'ATIVA' ? 'b-teal' : 'b-amber'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>{c._count.users}</td>
                    <td>{c._count.vehicles}</td>
                    <td>{c._count.routes}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => openUsers(c)}>
                        Gerenciar usuários
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} style={{ marginLeft: 6 }}>
                        Editar
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setConfirmStatus(c)} style={{ marginLeft: 6 }}>
                        {c.status === 'ATIVA' ? 'Suspender' : 'Reativar'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(c)} style={{ marginLeft: 6 }}>
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmStatus}
        onClose={() => setConfirmStatus(null)}
        title={confirmStatus?.status === 'ATIVA' ? 'Suspender empresa' : 'Reativar empresa'}
        danger={confirmStatus?.status === 'ATIVA'}
        message={
          <>
            {confirmStatus?.status === 'ATIVA' ? (
              <>
                Suspender <strong>{confirmStatus?.name}</strong>? Todos os usuários dessa empresa
                serão bloqueados no próximo login até você reativar.
              </>
            ) : (
              <>Reativar <strong>{confirmStatus?.name}</strong>? Os usuários voltam a conseguir logar.</>
            )}
          </>
        }
        confirmLabel={confirmStatus?.status === 'ATIVA' ? 'Suspender' : 'Reativar'}
        onConfirm={async () => { if (confirmStatus) await doToggleStatus(confirmStatus); }}
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Remover empresa PERMANENTEMENTE"
        danger
        message={
          <>
            Você está prestes a remover <strong>{confirmDelete?.name}</strong> e tudo que pertence a ela:
            <ul style={{ margin: '10px 0', paddingLeft: 20 }}>
              <li>{confirmDelete?._count.users} usuário(s)</li>
              <li>{confirmDelete?._count.vehicles} veículo(s)</li>
              <li>{confirmDelete?._count.routes} rota(s)</li>
              <li>Todos os depósitos, pontos de entrega, lançamentos e configurações</li>
            </ul>
            <strong style={{ color: '#b91c1c' }}>Esta ação não pode ser desfeita.</strong>
          </>
        }
        requireText={confirmDelete?.name}
        requireLabel="Digite o nome exato da empresa para confirmar"
        confirmLabel="Remover para sempre"
        onConfirm={async () => { if (confirmDelete) await doDelete(confirmDelete); }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Editar empresa — ${editing.name}` : ''}
        maxWidth="520px"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)} disabled={saving}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome da empresa</label>
          <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
        </div>
        <div className="field" style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label>CNPJ</label>
            <input className="input" value={editCnpj} onChange={(e) => setEditCnpj(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label>Telefone</label>
            <input className="input" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Plano</label>
          <select className="input" value={editPlan} onChange={(e) => setEditPlan(e.target.value)}>
            <option value="STANDARD">STANDARD</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nova empresa cliente"
        maxWidth="560px"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Criando…' : 'Criar'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome da empresa</label>
          <input className="input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </div>
        <div className="field" style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label>CNPJ (opcional)</label>
            <input className="input" value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label>Telefone (opcional)</label>
            <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Plano</label>
          <select className="input" value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="STANDARD">STANDARD</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
        <hr style={{ margin: '12px 0', opacity: 0.2 }} />
        <div className="field">
          <label>Nome do administrador da empresa</label>
          <input className="input" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </div>
        <div className="field">
          <label>Email do administrador</label>
          <input className="input" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
          <small style={{ opacity: 0.7 }}>
            Uma senha temporária será gerada. Envie ao administrador para o primeiro acesso.
          </small>
        </div>
      </Modal>

      <Modal
        isOpen={!!createResult}
        onClose={() => setCreateResult(null)}
        title="Empresa criada"
        footer={<button className="btn btn-primary" onClick={() => setCreateResult(null)}>Fechar</button>}
      >
        <p>Envie ao administrador da empresa cliente:</p>
        <div className="field">
          <label>Empresa</label>
          <input className="input" readOnly value={createResult?.company?.name || ''} />
        </div>
        <div className="field">
          <label>Email</label>
          <input className="input" readOnly value={createResult?.admin?.email || ''} />
        </div>
        <div className="field">
          <label>Senha temporária</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" readOnly value={createResult?.tempPassword || ''} />
            <button className="btn btn-secondary btn-sm" onClick={() => createResult && copy(createResult.tempPassword)}>Copiar</button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
